import { isTruthy } from 'notable-common';
import { z } from 'zod';
import { FlowBlockValueTemplateHint, validateFlowBlockValueTemplate, } from '../../../blockValue/template.js';
export var FlowDynamicValueType;
(function (FlowDynamicValueType) {
    /** A JSON literal value or a non-stringified template placeholder */
    FlowDynamicValueType["Auto"] = "Auto";
    // Auto should now replace all the other options:
    /** The `data` of an upstream block, specified by name */
    FlowDynamicValueType["Block"] = "Block";
    /** The current array element within an Iterator block */
    FlowDynamicValueType["Element"] = "Element";
    /** An object path (e.g., `patient.data.firstName`) */
    FlowDynamicValueType["Expression"] = "Expression";
    /** A JSON literal value (e.g., `[true, false]`) */
    FlowDynamicValueType["Literal"] = "Literal";
    /** Templatized text (e.g., `Hello, {{patient.data.firstName}}!` */
    FlowDynamicValueType["Template"] = "Template";
})(FlowDynamicValueType || (FlowDynamicValueType = {}));
export const FlowDynamicValueSchema = z
    .discriminatedUnion('type', [
    z.object({
        type: z.undefined(),
    }),
    z.object({
        type: z.literal(FlowDynamicValueType.Auto),
        jsonOrTemplate: z.string().optional(),
    }),
    z.object({
        type: z.literal(FlowDynamicValueType.Block),
        blockName: z.string().min(1, 'Block is required'),
    }),
    z.object({
        type: z.literal(FlowDynamicValueType.Element),
    }),
    z.object({
        type: z.literal(FlowDynamicValueType.Expression),
        expression: z
            .string()
            .refine((value) => !value.startsWith('{{'), 'Do not use block templates like {{blockName.data}}. i.e. Remove `{{ }}` '),
    }),
    z.object({
        type: z.literal(FlowDynamicValueType.Literal),
        value: z.unknown().optional(),
        json: z.string().optional(),
    }),
    z.object({
        type: z.literal(FlowDynamicValueType.Template),
        template: z.string(),
    }),
])
    .superRefine((value, context) => {
    if (value.type === FlowDynamicValueType.Auto) {
        const { jsonOrTemplate } = value;
        if (isTruthy(jsonOrTemplate)) {
            if (jsonOrTemplate.startsWith('{{')) {
                const result = validateFlowBlockValueTemplate(jsonOrTemplate);
                if (result.hint === FlowBlockValueTemplateHint.NotTemplateLike) {
                    context.addIssue({
                        code: 'custom',
                        message: 'Must end with }}',
                    });
                }
                else if (result.hint === FlowBlockValueTemplateHint.Invalid) {
                    context.addIssue({
                        code: 'custom',
                        message: result.message,
                    });
                }
            }
            else {
                try {
                    JSON.parse(jsonOrTemplate);
                }
                catch (error) {
                    context.addIssue({
                        code: 'custom',
                        message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
                    });
                }
            }
        }
    }
    else if (value.type === FlowDynamicValueType.Literal) {
        let parsedValue = undefined;
        try {
            parsedValue = isTruthy(value.json) ? JSON.parse(value.json) : undefined;
        }
        catch (error) {
            context.addIssue({
                code: 'custom',
                message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
                path: ['json'],
            });
            return;
        }
        // `json` must be valid JSON and must parse to `value`
        if (JSON.stringify(parsedValue) !== JSON.stringify(value.value)) {
            context.addIssue({
                code: 'custom',
                message: `"value" does not match parsed value of "json"`,
            });
        }
    }
});
//# sourceMappingURL=dynamicValue.js.map